import { bind } from "@react-rxjs/core"
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  scan,
  shareReplay,
  startWith,
  switchMap,
} from "rxjs"

import type { BarResponse } from "@/App/Equities/shared/services/TradingGateway"
import { watchlistInstruments$ } from "@/App/Equities/shared/services/watchlist"

import { getLatestBars$, getLatestQuote$ } from "./marketData"

type Quote = {
  bidPrice: number
  askPrice: number
  timestamp: string
}

type QuoteBySymbol = Record<string, Quote>
type BarResponseBySymbol = Record<string, BarResponse>

// 1. Derive the comma-separated symbols string from the watchlist
export const watchlistSymbols$: Observable<string> = watchlistInstruments$.pipe(
  map((lists) => lists.map((a) => a.symbol).join(",")),
  distinctUntilChanged(),
)

// 2. Single Hydra subscription for ALL watchlist symbols
const allQuotes$ = watchlistSymbols$.pipe(
  switchMap((symbols) =>
    getLatestQuote$({ symbols }).pipe(
      map(({ ap, bp, symbol, t }) => ({
        askPrice: Number(ap),
        bidPrice: Number(bp),
        timestamp: t,
        symbol,
      })),
      scan<{ symbol: string } & Quote, QuoteBySymbol>(
        (acc, curr) => ({
          ...acc,
          [curr.symbol]: curr,
        }),
        {},
      ),
    ),
  ),
  shareReplay({ bufferSize: 1, refCount: true }),
)

const allLatestBars$ = watchlistSymbols$.pipe(
  switchMap((symbols) =>
    getLatestBars$({ symbols }).pipe(
      scan<BarResponse, BarResponseBySymbol>(
        (acc, curr) => ({
          ...acc,
          [curr.symbol]: curr,
        }),
        {},
      ),
    ),
  ),
  shareReplay({ bufferSize: 1, refCount: true }),
)

// 3. Per-symbol accessors — filters from the shared stream, no extra Hydra request
export const [useLatestQuote, latestQuote$] = bind((symbol: string) =>
  allQuotes$.pipe(
    map((quotes) => quotes[symbol]),
    filter((q): q is Quote => q != null), // catches both null AND undefined
    startWith({
      bidPrice: 0,
      askPrice: 0,
      timestamp: "",
    } as Quote),
  ),
)

// Note: this is a one-minute bar, not a daily bar
export const latestBars$ = (symbol: string) =>
  allLatestBars$.pipe(
    map((bars) => bars[symbol]),
    filter((b): b is BarResponse => b != null), // catches both null AND undefined
    distinctUntilChanged(),
    startWith({
      o: "0",
      h: "0",
      l: "0",
      c: "0",
      v: 0,
      n: 0,
      vw: "0",
      t: "",
      symbol,
    } as BarResponse),
  )
